import React, { useEffect, useState, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import BoatCard from '../components/BoatCard'
import SEO from '../components/SEO'
import { getBoatType, getBoatStatus } from '../lib/utils'

// In-memory cache so navigating between boat detail and overview is instant
let cachedBoats = null

function getInitialFilters(categoryFilter, searchParams, storageKey) {
  // 1. First priority: URL search parameters
  const paramType = searchParams.get('type') || searchParams.get('kategori')
  const paramStatus = searchParams.get('status')
  const paramMinPrice = searchParams.get('minPrice') || searchParams.get('minPris')
  const paramMaxPrice = searchParams.get('maxPrice') || searchParams.get('maxPris')
  const paramSort = searchParams.get('sort') || searchParams.get('sortering')

  const hasAnyParam = Boolean(paramType || paramStatus || paramMinPrice || paramMaxPrice || paramSort)

  if (hasAnyParam) {
    return {
      category: categoryFilter || paramType || 'Alle',
      status: paramStatus || 'Alle',
      minPrice: paramMinPrice || '',
      maxPrice: paramMaxPrice || '',
      sort: paramSort || 'nyeste'
    }
  }

  // 2. Second priority: sessionStorage
  try {
    const saved = sessionStorage.getItem(storageKey)
    if (saved) {
      const parsed = JSON.parse(saved)
      return {
        category: categoryFilter || parsed.category || 'Alle',
        status: parsed.status || 'Alle',
        minPrice: parsed.minPrice || '',
        maxPrice: parsed.maxPrice || '',
        sort: parsed.sort || 'nyeste'
      }
    }
  } catch (e) {}

  // 3. Fallback defaults
  return {
    category: categoryFilter || 'Alle',
    status: 'Alle',
    minPrice: '',
    maxPrice: '',
    sort: 'nyeste'
  }
}

export default function BoatsPage({ categoryFilter = null, titleOverride = null }) {
  const [searchParams, setSearchParams] = useSearchParams()

  const storageKey = categoryFilter
    ? `nordfyn_boats_filters_${categoryFilter.toLowerCase()}`
    : 'nordfyn_boats_filters'

  const [boats, setBoats] = useState(() => cachedBoats || [])
  const [loading, setLoading] = useState(() => !cachedBoats)

  // Initial filter values
  const initialFilters = useMemo(() => {
    return getInitialFilters(categoryFilter, searchParams, storageKey)
  }, []) // Only on mount

  // Filters state
  const [selectedCategory, setSelectedCategory] = useState(initialFilters.category)
  const [selectedStatus, setSelectedStatus] = useState(initialFilters.status)
  const [minPrice, setMinPrice] = useState(initialFilters.minPrice)
  const [maxPrice, setMaxPrice] = useState(initialFilters.maxPrice)
  const [selectedSort, setSelectedSort] = useState(initialFilters.sort)

  const isResettingRef = useRef(false)
  const hasRestoredScroll = useRef(false)

  // Fetch boats (background revalidation if cached, immediate if not)
  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/v2/content/baade?limit=100')
      .then((res) => res.json())
      .then((data) => {
        if (data && data.items) {
          cachedBoats = data.items
          setBoats(data.items)
        }
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [])

  // Sync state if URL search parameters change (e.g. browser Back/Forward)
  useEffect(() => {
    if (isResettingRef.current) {
      isResettingRef.current = false
      return
    }

    const paramType = searchParams.get('type') || searchParams.get('kategori')
    const paramStatus = searchParams.get('status')
    const paramMinPrice = searchParams.get('minPrice') || searchParams.get('minPris')
    const paramMaxPrice = searchParams.get('maxPrice') || searchParams.get('maxPris')
    const paramSort = searchParams.get('sort') || searchParams.get('sortering')

    const hasAnyParam = Boolean(paramType || paramStatus || paramMinPrice || paramMaxPrice || paramSort)

    if (hasAnyParam) {
      const targetCategory = categoryFilter || paramType || 'Alle'
      const targetStatus = paramStatus || 'Alle'
      const targetMinPrice = paramMinPrice || ''
      const targetMaxPrice = paramMaxPrice || ''
      const targetSort = paramSort || 'nyeste'

      setSelectedCategory(targetCategory)
      setSelectedStatus(targetStatus)
      setMinPrice(targetMinPrice)
      setMaxPrice(targetMaxPrice)
      setSelectedSort(targetSort)
    } else {
      // If URL has no params, check if we have saved filters in sessionStorage
      try {
        const saved = sessionStorage.getItem(storageKey)
        if (saved) {
          const parsed = JSON.parse(saved)
          const targetCategory = categoryFilter || parsed.category || 'Alle'
          const targetStatus = parsed.status || 'Alle'
          const targetMinPrice = parsed.minPrice || ''
          const targetMaxPrice = parsed.maxPrice || ''
          const targetSort = parsed.sort || 'nyeste'

          setSelectedCategory(targetCategory)
          setSelectedStatus(targetStatus)
          setMinPrice(targetMinPrice)
          setMaxPrice(targetMaxPrice)
          setSelectedSort(targetSort)
          return
        }
      } catch (e) {}

      // Clean default state
      setSelectedCategory(categoryFilter || 'Alle')
      setSelectedStatus('Alle')
      setMinPrice('')
      setMaxPrice('')
      setSelectedSort('nyeste')
    }
  }, [searchParams, categoryFilter, storageKey])

  // Lock category if prop is provided
  useEffect(() => {
    if (categoryFilter) {
      setSelectedCategory(categoryFilter)
    }
  }, [categoryFilter])

  // Sync filters to URL searchParams & sessionStorage (debounced to ensure smooth price typing)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isResettingRef.current) return

      const params = new URLSearchParams()

      if (!categoryFilter && selectedCategory && selectedCategory !== 'Alle') {
        params.set('type', selectedCategory)
      }
      if (selectedStatus && selectedStatus !== 'Alle') {
        params.set('status', selectedStatus)
      }
      if (minPrice) {
        params.set('minPrice', minPrice)
      }
      if (maxPrice) {
        params.set('maxPrice', maxPrice)
      }
      if (selectedSort && selectedSort !== 'nyeste') {
        params.set('sort', selectedSort)
      }

      // Persist to sessionStorage if any filter is non-default
      const hasAnyNonDefault =
        (!categoryFilter && selectedCategory !== 'Alle') ||
        selectedStatus !== 'Alle' ||
        Boolean(minPrice) ||
        Boolean(maxPrice) ||
        selectedSort !== 'nyeste'

      try {
        if (hasAnyNonDefault) {
          const filterState = {
            category: selectedCategory,
            status: selectedStatus,
            minPrice,
            maxPrice,
            sort: selectedSort
          }
          sessionStorage.setItem(storageKey, JSON.stringify(filterState))
          const q = params.toString()
          sessionStorage.setItem('nordfyn_boats_filters_query', q ? `?${q}` : '')
        } else {
          sessionStorage.removeItem(storageKey)
          sessionStorage.removeItem('nordfyn_boats_filters_query')
        }
      } catch (e) {}

      // Update URL search parameters if different
      const currentParamsString = searchParams.toString()
      const newParamsString = params.toString()

      if (currentParamsString !== newParamsString) {
        setSearchParams(params, { replace: true })
      }
    }, 150)

    return () => clearTimeout(timer)
  }, [
    selectedCategory,
    selectedStatus,
    minPrice,
    maxPrice,
    selectedSort,
    categoryFilter,
    storageKey,
    searchParams,
    setSearchParams
  ])

  // Save scroll position on unmount / beforeunload so returning restores exact spot
  useEffect(() => {
    const handleSaveScroll = () => {
      try {
        if (window.scrollY > 50) {
          sessionStorage.setItem('nordfyn_boats_scroll_pos', String(window.scrollY))
        }
      } catch (e) {}
    }

    window.addEventListener('beforeunload', handleSaveScroll)
    return () => {
      handleSaveScroll()
      window.removeEventListener('beforeunload', handleSaveScroll)
    }
  }, [])

  // Restore scroll position once boats are available
  useEffect(() => {
    if (!loading && !hasRestoredScroll.current) {
      hasRestoredScroll.current = true
      try {
        const savedScroll = sessionStorage.getItem('nordfyn_boats_scroll_pos')
        if (savedScroll) {
          const scrollY = parseInt(savedScroll, 10)
          if (!isNaN(scrollY) && scrollY > 0) {
            sessionStorage.removeItem('nordfyn_boats_scroll_pos')
            requestAnimationFrame(() => {
              window.scrollTo({ top: scrollY, behavior: 'instant' })
            })
          }
        }
      } catch (e) {}
    }
  }, [loading])

  // Filter & Sort Logic
  const filteredBoats = useMemo(() => {
    let result = [...boats]

    // Category filter
    if (selectedCategory && selectedCategory !== 'Alle') {
      result = result.filter((b) => {
        const type = getBoatType(b)
        return type && type.toLowerCase() === selectedCategory.toLowerCase()
      })
    }

    // Status filter
    if (selectedStatus && selectedStatus !== 'Alle') {
      result = result.filter((b) => {
        const st = getBoatStatus(b)
        return st && st.toLowerCase() === selectedStatus.toLowerCase()
      })
    }

    // Price Filter
    if (minPrice || maxPrice) {
      result = result.filter((b) => {
        const numericPrice = parseInt(String(b.pris || '').replace(/\D/g, ''), 10)
        if (isNaN(numericPrice)) return true
        if (minPrice && numericPrice < parseInt(minPrice, 10)) return false
        if (maxPrice && numericPrice > parseInt(maxPrice, 10)) return false
        return true
      })
    }

    // Sort
    result.sort((a, b) => {
      if (selectedSort === 'pris-stigende') {
        const priceA = parseInt(String(a.pris || '').replace(/\D/g, ''), 10) || 0
        const priceB = parseInt(String(b.pris || '').replace(/\D/g, ''), 10) || 0
        return priceA - priceB
      }
      if (selectedSort === 'pris-faldende') {
        const priceA = parseInt(String(a.pris || '').replace(/\D/g, ''), 10) || 0
        const priceB = parseInt(String(b.pris || '').replace(/\D/g, ''), 10) || 0
        return priceB - priceA
      }
      if (selectedSort === 'aargang') {
        return (b.aargang || 0) - (a.aargang || 0)
      }
      if (selectedSort === 'model') {
        return (a.title || '').localeCompare(b.title || '')
      }
      // 'nyeste' default: Sort by sagsnummer (highest first), fallback to updatedAt
      const caseA = parseInt(String(a.sagsnummer || '').replace(/\D/g, ''), 10)
      const caseB = parseInt(String(b.sagsnummer || '').replace(/\D/g, ''), 10)
      if (!isNaN(caseA) && !isNaN(caseB) && caseA !== caseB) {
        return caseB - caseA
      }
      if (!isNaN(caseA) && isNaN(caseB)) return -1
      if (isNaN(caseA) && !isNaN(caseB)) return 1

      const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0
      const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0
      if (dateA !== dateB) {
        return dateB - dateA
      }

      return (b.id || '').localeCompare(a.id || '')
    })

    return result
  }, [boats, selectedCategory, selectedStatus, minPrice, maxPrice, selectedSort])

  const resetFilters = () => {
    isResettingRef.current = true
    const defaultCat = categoryFilter || 'Alle'
    setSelectedCategory(defaultCat)
    setSelectedStatus('Alle')
    setMinPrice('')
    setMaxPrice('')
    setSelectedSort('nyeste')

    try {
      sessionStorage.removeItem(storageKey)
      sessionStorage.removeItem('nordfyn_boats_filters_query')
    } catch (e) {}

    setSearchParams({}, { replace: true })
  }

  const hasActiveFilters =
    (!categoryFilter && selectedCategory !== 'Alle') ||
    selectedStatus !== 'Alle' ||
    Boolean(minPrice) ||
    Boolean(maxPrice) ||
    selectedSort !== 'nyeste'

  const pageTitle =
    titleOverride ||
    (selectedCategory !== 'Alle' ? `${selectedCategory} til salg` : 'Alle både til salg')

  const fullSeoTitle = `${pageTitle} - Nordfyn Marine`

  return (
    <div className="py-12 bg-slate-50 min-h-screen">
      <SEO
        title={fullSeoTitle}
        description="Se alle både til salg hos Nordfyn Marine. Motorbåde, sejlbåde og motorsejlere med grundig rådgivning."
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        {/* Header */}
        <div className="bg-[#1E7BC1] text-white p-8 sm:p-10 rounded-2xl shadow-lg relative overflow-hidden">
          <div className="relative z-10 max-w-2xl space-y-3">
            <span className="text-xs font-bold uppercase tracking-widest text-[#2BABE3]">
              Aktuelt udvalg
            </span>
            <h1 className="text-3xl sm:text-4xl font-serif font-bold text-white">
              {pageTitle}
            </h1>
            <p className="text-slate-300 text-sm">
              Find din næste drømmebåd blandt vores udvalg af brugte motorbåde, motorsejlere og sejlbåde. Alle både formidles med sikker købsaftale og faglig rådgivning.
            </p>
          </div>
        </div>

        {/* Filter and Sort Bar */}
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Category Filter */}
            {!categoryFilter && (
              <div>
                <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-[0.2em] mb-1.5">
                  Bådtype
                </label>
                <div className="relative">
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs font-medium text-slate-900 appearance-none focus:outline-none focus:border-[#1E7BC1] focus:bg-white transition-all cursor-pointer"
                  >
                    <option value="Alle">Alle typer</option>
                    <option value="Motorbåd">Motorbåde</option>
                    <option value="Motorsejler">Motorsejlere</option>
                    <option value="Sejlbåd">Sejlbåde</option>
                  </select>
                  <i className="fa-solid fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-[10px] pointer-events-none" />
                </div>
              </div>
            )}

            {/* Status Filter */}
            <div>
              <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-[0.2em] mb-1.5">
                Status
              </label>
              <div className="relative">
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs font-medium text-slate-900 appearance-none focus:outline-none focus:border-[#1E7BC1] focus:bg-white transition-all cursor-pointer"
                >
                  <option value="Alle">Alle statusser</option>
                  <option value="Til salg">Til salg</option>
                  <option value="Nyhed">Nyheder</option>
                  <option value="Nedsat">Nedsat</option>
                  <option value="Reserveret">Reserveret</option>
                  <option value="Solgt">Solgt</option>
                </select>
                <i className="fa-solid fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-[10px] pointer-events-none" />
              </div>
            </div>

            {/* Min Price */}
            <div>
              <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-[0.2em] mb-1.5">
                Min. pris (DKK)
              </label>
              <input
                type="number"
                placeholder="f.eks. 100000"
                value={minPrice}
                onChange={(e) => setMinPrice(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs font-medium text-slate-900 focus:outline-none focus:border-[#1E7BC1] focus:bg-white transition-all placeholder:text-slate-400"
              />
            </div>

            {/* Max Price */}
            <div>
              <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-[0.2em] mb-1.5">
                Maks. pris (DKK)
              </label>
              <input
                type="number"
                placeholder="f.eks. 500000"
                value={maxPrice}
                onChange={(e) => setMaxPrice(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs font-medium text-slate-900 focus:outline-none focus:border-[#1E7BC1] focus:bg-white transition-all placeholder:text-slate-400"
              />
            </div>

            {/* Sortering */}
            <div>
              <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-[0.2em] mb-1.5">
                Sorter efter
              </label>
              <div className="relative">
                <select
                  value={selectedSort}
                  onChange={(e) => setSelectedSort(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs font-medium text-slate-900 appearance-none focus:outline-none focus:border-[#1E7BC1] focus:bg-white transition-all cursor-pointer"
                >
                  <option value="nyeste">Nyeste først</option>
                  <option value="pris-stigende">Pris: Lav til høj</option>
                  <option value="pris-faldende">Pris: Høj til lav</option>
                  <option value="aargang">Årgang (Nyeste år)</option>
                  <option value="model">Model A-Å</option>
                </select>
                <i className="fa-solid fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-[10px] pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Active Filters Badges */}
          {hasActiveFilters && (
            <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-100 text-xs">
              <span className="text-slate-400 font-medium text-[11px] uppercase tracking-wider mr-1">
                Aktive filtre:
              </span>
              {!categoryFilter && selectedCategory !== 'Alle' && (
                <button
                  type="button"
                  onClick={() => setSelectedCategory('Alle')}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-sky-50 text-[#1E7BC1] border border-sky-200 text-xs hover:bg-sky-100 transition-colors cursor-pointer"
                >
                  <span>{selectedCategory}</span>
                  <i className="fa-solid fa-xmark text-[10px]" />
                </button>
              )}
              {selectedStatus !== 'Alle' && (
                <button
                  type="button"
                  onClick={() => setSelectedStatus('Alle')}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-sky-50 text-[#1E7BC1] border border-sky-200 text-xs hover:bg-sky-100 transition-colors cursor-pointer"
                >
                  <span>Status: {selectedStatus}</span>
                  <i className="fa-solid fa-xmark text-[10px]" />
                </button>
              )}
              {minPrice && (
                <button
                  type="button"
                  onClick={() => setMinPrice('')}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-sky-50 text-[#1E7BC1] border border-sky-200 text-xs hover:bg-sky-100 transition-colors cursor-pointer"
                >
                  <span>Min: {parseInt(minPrice, 10).toLocaleString('da-DK')} kr.</span>
                  <i className="fa-solid fa-xmark text-[10px]" />
                </button>
              )}
              {maxPrice && (
                <button
                  type="button"
                  onClick={() => setMaxPrice('')}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-sky-50 text-[#1E7BC1] border border-sky-200 text-xs hover:bg-sky-100 transition-colors cursor-pointer"
                >
                  <span>Maks: {parseInt(maxPrice, 10).toLocaleString('da-DK')} kr.</span>
                  <i className="fa-solid fa-xmark text-[10px]" />
                </button>
              )}
              {selectedSort !== 'nyeste' && (
                <button
                  type="button"
                  onClick={() => setSelectedSort('nyeste')}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-100 text-slate-700 border border-slate-200 text-xs hover:bg-slate-200 transition-colors cursor-pointer"
                >
                  <span>
                    Sortering:{' '}
                    {selectedSort === 'pris-stigende'
                      ? 'Pris (lav-høj)'
                      : selectedSort === 'pris-faldende'
                      ? 'Pris (høj-lav)'
                      : selectedSort === 'aargang'
                      ? 'Årgang'
                      : 'Model A-Å'}
                  </span>
                  <i className="fa-solid fa-xmark text-[10px]" />
                </button>
              )}
              <button
                type="button"
                onClick={resetFilters}
                className="text-xs text-slate-500 hover:text-red-600 underline underline-offset-2 ml-1 cursor-pointer transition-colors"
              >
                Ryd alle
              </button>
            </div>
          )}

          {/* Results Bar */}
          <div className="flex flex-wrap justify-between items-center pt-4 border-t border-slate-100 text-xs text-slate-600 gap-3">
            <div className="font-semibold text-slate-900 tracking-wider text-xs uppercase">
              Viser <span className="text-[#1E7BC1] font-bold text-sm mx-1">{filteredBoats.length}</span> båd(e)
            </div>

            <button
              type="button"
              onClick={resetFilters}
              className={`text-[11px] uppercase tracking-[0.15em] font-semibold flex items-center gap-2 transition-colors border px-3.5 py-1.5 rounded-xl cursor-pointer ${
                hasActiveFilters
                  ? 'bg-sky-50 text-[#1E7BC1] border-sky-200 hover:bg-sky-100'
                  : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-white hover:text-[#1E7BC1]'
              }`}
            >
              <i className="fa-solid fa-rotate-right text-[10px]" />
              <span>Nulstil filtre</span>
            </button>
          </div>
        </div>

        {/* Boats List */}
        {loading ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-slate-200">
            <i className="fa-solid fa-spinner animate-spin text-4xl text-[#2BABE3]" />
            <p className="text-xs text-slate-500 mt-3 font-medium">Henter både...</p>
          </div>
        ) : filteredBoats.length > 0 ? (
          <div className="space-y-6">
            {filteredBoats.map((boat) => (
              <BoatCard key={boat.id || boat.url_slug} boat={boat} layout="row" />
            ))}
          </div>
        ) : (
          <div className="bg-white p-12 rounded-2xl border border-slate-200 text-center space-y-4 max-w-xl mx-auto shadow-sm">
            <div className="w-16 h-16 bg-[#1E7BC1]/10 text-[#1E7BC1] rounded-full flex items-center justify-center mx-auto text-2xl">
              <i className="fa-solid fa-ship" />
            </div>
            <h3 className="font-serif font-bold text-2xl text-slate-900">
              Ingen både matchede dine søgekriterier
            </h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Vi modtager løbende nye både til salg. Kontakt os gerne med din søgning, så kan vi hjælpe dig med at finde båden.
            </p>
            <div className="pt-2 flex flex-wrap justify-center gap-3">
              <button
                type="button"
                onClick={resetFilters}
                className="bg-[#1E7BC1] text-white px-4 py-2 rounded-lg text-xs font-bold hover:bg-[#2BABE3] transition-colors cursor-pointer"
              >
                Nulstil filtre
              </button>
              <a
                href="tel:40207034"
                className="bg-[#2BABE3] text-white px-4 py-2 rounded-lg text-xs font-bold hover:bg-[#1E7BC1] transition-colors whitespace-nowrap"
              >
                Ring 40 20 70 34
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
